import { Tooltip } from "@wayflyer/flyui";
import { DataTable, filterRows, useClientFilters, useTableStateReducer } from "@wayflyer/flyui-dataviz";
import type {
  ColumnRenderParams,
  ControlOptions,
  DataTableColumn,
  LegacyTableProps,
  LegacyTableRow,
  RowActions,
  TableState,
} from "@wayflyer/flyui-dataviz";
import { IconImport16Line } from "@wayflyer/flyui-icons/16/line";
import type { ReactNode } from "react";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useIntl } from "react-intl";

import { downloadCsv } from "./csv";
import { useColumnVisibilityStorage } from "./use-column-visibility-storage";

type Row = LegacyTableRow;

/** Mirrors DataTable's `controlOptions.filterOptions.customSort` value union. */
type CustomSortEntry =
  | keyof Row
  | ((a: string, b: string) => number)
  | { compareRows: (rowA: Row, rowB: Row, direction: "asc" | "desc") => number };

const SORT_PREFIX = "sort_";
const DEFAULT_PAGE_SIZE = 10;

/**
 * Adapter that renders the insights tables (built in the legacy column/row
 * shape) using the DataTable component. Search, column visibility, sorting and
 * pagination are handled client-side and surfaced through DataTable's toolbar.
 *
 * It is deliberately a drop-in replacement for the previous `LegacyTable`
 * usages, accepting the same props the existing data builders already produce.
 *
 * Some LegacyTable props have no DataTable equivalent and are intentionally
 * ignored (accepted behaviour change after the migration):
 * - `size` ("small" | "large"): DataTable has no density preset; rows use the
 *   default height, so previously-compact tables render slightly taller.
 * - `sortDescFirst` (per column): DataTable headers always sort ascending on the
 *   first click. The initial sort direction is still honoured (via `sortBy`), but
 *   clicking a metric column to re-sort now starts ascending rather than descending.
 * - `disableSortRemove`: DataTable's sort cycles asc -> desc -> cleared, so a third
 *   click can return the table to its unsorted (data) order.
 * - `getRowStyle`: per-row inline styling isn't supported. It was only used to bold
 *   a summary row, which is now handled by the dedicated `summaryRow` prop.
 * - `columnSelectorLabel` / `columnSelectorShowSearch` / `columnSelectorSearchPlaceholder`:
 *   DataTable's column-config menu has no equivalent options. The show/hide column
 *   control still works (via `controlOptions.hideableColumns`); only this cosmetic
 *   customisation of the menu is dropped.
 */
export const InsightsDataTable = ({
  columns: legacyColumns,
  data,
  initialPageSize = DEFAULT_PAGE_SIZE,
  enablePagination = true,
  sortBy,
  title,
  getToolTipForColumn,
  getToolTipForData,
  onClickLink,
  sortRows,
  enableSearch = false,
  searchPlaceholder = "",
  enableColumnSelector = false,
  tableSettingsStoragePath = "",
  enableCSVDownload = false,
  csvFilename = "",
  summaryRow,
  onSearchChangeHandler,
}: LegacyTableProps) => {
  const intl = useIntl();
  const rows = useMemo(() => (Array.isArray(data) ? data : []), [data]);

  // Columns included in text search. Drives both the search toolbar and the
  // per-column tooltip decision below.
  const searchableColumns = useMemo(
    () => (enableSearch ? legacyColumns.filter((col) => col.disableSearch !== true).map((col) => col.id) : []),
    [enableSearch, legacyColumns]
  );
  const searchableColumnIds = useMemo(() => new Set(searchableColumns.map(String)), [searchableColumns]);

  const columns = useMemo<DataTableColumn<Row>[]>(
    () =>
      legacyColumns.map((col): DataTableColumn<Row> => {
        const headerTooltip = getToolTipForColumn?.(col.id) || undefined;
        const link = col.isLink && onClickLink ? { onClick: (row: Row) => onClickLink(row, col.id) } : undefined;
        // A per-cell tooltip needs a custom cell renderer, which shadows the
        // built-in search highlighting. Apply it per column so searchable columns
        // keep highlighting while other columns can still show `getToolTipForData`.
        const applyTooltip = !!getToolTipForData && !searchableColumnIds.has(String(col.id));
        const render = applyTooltip
          ? (context: ColumnRenderParams<Row>, colId: keyof Row) => {
              const value = context.getValue() as ReactNode;
              const tooltip = getToolTipForData?.(context.row.original, colId);
              return tooltip ? (
                <Tooltip content={tooltip} asChild>
                  <span>{value}</span>
                </Tooltip>
              ) : (
                <>{value}</>
              );
            }
          : undefined;

        return {
          id: col.id,
          name: col.name,
          align: col.align,
          headerTooltip,
          disableVisibilityToggle: col.allowSelection === false,
          link,
          render,
        };
      }),
    [legacyColumns, getToolTipForColumn, getToolTipForData, onClickLink, searchableColumnIds]
  );

  // When pagination is off, use an unbounded page size so all rows are always shown
  // even if the dataset grows after mount (the page size is frozen into the initial
  // reducer state, so a fixed row count would otherwise hide later rows).
  const pageSize = enablePagination ? initialPageSize : Number.MAX_SAFE_INTEGER;

  const defaultTableState = useMemo<TableState<Row>>(
    () => ({
      sortBy: sortBy?.[0] ? { id: sortBy[0].id as keyof Row, direction: sortBy[0].desc ? "desc" : "asc" } : undefined,
      pagination: { pageIndex: 0, pageSize },
    }),
    // `pageSize`/`sortBy` form the initial state only; later changes are owned by the reducer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const { tableState, dispatchTableStateChange } = useTableStateReducer<Row>({ defaultTableState });

  const filterOptions = useMemo(() => {
    const customSort: Record<string, CustomSortEntry> = {};

    if (sortRows) {
      // Honour the table's row comparator (e.g. Products' low-volume demotion,
      // which reads a sibling quantity column). Legacy comparators return an
      // ascending-oriented value and rely on react-table reversing for "desc";
      // `compareRows` is used directly, so we negate for "desc" to match.
      legacyColumns.forEach((col) => {
        customSort[col.id as string] = {
          compareRows: (rowA, rowB, direction) => {
            const desc = direction === "desc";
            const result = sortRows(
              { original: rowA } as Parameters<NonNullable<typeof sortRows>>[0],
              { original: rowB } as Parameters<NonNullable<typeof sortRows>>[1],
              col.id,
              desc
            );
            return desc ? -result : result;
          },
        };
      });
      return { customSort };
    }

    // Otherwise sort numeric columns by their raw `sort_<id>` value rather than
    // the formatted display string, mirroring LegacyTable's default sorting.
    legacyColumns.forEach((col) => {
      const sortKey = `${SORT_PREFIX}${String(col.id)}`;
      if (rows.some((row) => sortKey in row)) {
        customSort[col.id as string] = sortKey;
      }
    });
    return { customSort };
  }, [legacyColumns, rows, sortRows]);

  const { initialHidden, onHiddenColumnsChange } = useColumnVisibilityStorage({
    columns: legacyColumns,
    storagePath: tableSettingsStoragePath,
    enabled: enableColumnSelector,
  });

  const controlOptions = useMemo<ControlOptions<Row>>(() => {
    const sortable = legacyColumns.filter((col) => !col.disableSort).map((col) => col.id);
    return {
      sortable,
      searchable: enableSearch ? searchableColumns : undefined,
      searchFieldPlaceholder: searchPlaceholder || undefined,
      hidePagination: !enablePagination,
      filterOptions,
      hideableColumns: enableColumnSelector ? { enabled: true, initialHidden, onHiddenColumnsChange } : undefined,
    };
  }, [
    legacyColumns,
    enableSearch,
    searchableColumns,
    searchPlaceholder,
    enablePagination,
    filterOptions,
    enableColumnSelector,
    initialHidden,
    onHiddenColumnsChange,
  ]);

  const {
    data: pageRows,
    pageCount,
    rowCount,
  } = useClientFilters<Row>({ tableState, data: rows, columns, filterOptions });

  // When a search-aware summary recompute handler is supplied (products), feed it
  // the filtered (unpaginated) rows. Recompute whenever the search term OR the
  // underlying data changes (so the summary doesn't go stale when rows refresh
  // under the same term), but skip sort/pagination-only changes. The filtering
  // mirrors the table exactly: same `filterRows` + `filterOptions`, only without
  // pagination so the summary aggregates the full filtered set.
  const lastSummaryInputs = useRef<{ term: string; rows: Row[] } | null>(null);
  useEffect(() => {
    if (!onSearchChangeHandler) {
      return;
    }
    const term = tableState.search?.term ?? "";
    if (lastSummaryInputs.current?.term === term && lastSummaryInputs.current?.rows === rows) {
      return;
    }
    lastSummaryInputs.current = { term, rows };
    const { rows: filtered } = filterRows<Row>({
      data: rows,
      tableState: { ...tableState, pagination: undefined },
      filterOptions,
    });
    onSearchChangeHandler(term, filtered);
  }, [tableState, rows, filterOptions, onSearchChangeHandler]);

  // Pin only the columns explicitly marked sticky via legacy `stickyParams`
  // (left/right sticky columns sit at the edges in the insights tables).
  const { pinLeftColCount, pinRightColCount } = useMemo(
    () => ({
      pinLeftColCount: legacyColumns.filter((col) => col.stickyParams?.align === "left").length,
      pinRightColCount: legacyColumns.filter((col) => col.stickyParams?.align === "right").length,
    }),
    [legacyColumns]
  );

  // Row identity must be unique within the table — DataTable uses it for React
  // keys. The first column is often a non-unique label (e.g. two ad campaigns
  // sharing a display name), so derive the id from the full row content instead.
  const getRowId = useCallback((row: Row) => JSON.stringify(row), []);

  const rowActions = useMemo<RowActions<Row> | undefined>(() => {
    if (!enableCSVDownload) {
      return undefined;
    }
    return {
      primaryAction: {
        content: intl.formatMessage({ id: "insights table: Download", defaultMessage: "Download" }),
        iconRight: IconImport16Line,
        variant: "Tertiary",
        onAction: () => downloadCsv(csvFilename, legacyColumns, rows),
      },
    };
  }, [enableCSVDownload, csvFilename, legacyColumns, rows, intl]);

  const summaryRowConfig =
    summaryRow && Object.keys(summaryRow).length > 0 ? { row: summaryRow, position: "top" as const } : undefined;

  return (
    <DataTable<Row>
      getRowId={getRowId}
      columns={columns}
      data={pageRows}
      title={typeof title === "string" && title ? title : undefined}
      pageCount={pageCount}
      rowCount={rowCount}
      tableState={tableState}
      defaultTableState={defaultTableState}
      dispatchTableStateChange={dispatchTableStateChange}
      controlOptions={controlOptions}
      rowActions={rowActions}
      summaryRow={summaryRowConfig}
      pinLeftColCount={pinLeftColCount}
      pinRightColCount={pinRightColCount}
    />
  );
};
